// src/app/shared/split.service.ts
import { Injectable, signal } from '@angular/core';
import { SplitOption } from '@tabify/models/types';

@Injectable({ providedIn: 'root' })
export class SplitService {
  splitCount = signal<number>(2); // default split count
  hideSplitCountControls = signal<boolean>(false);
  payForSplitCount = signal<number>(1);
  maxPayForSplitCount = signal<number | null>(null);
  hidePayForSplitCountControls = signal<boolean>(false);

  /**
   * Calculate the fair split amount for specific payer positions.
   * Distributes remainder øre to the first payers to ensure total adds up exactly.
   *
   * Example: 200 kr ÷ 3 people
   * - Base: floor(20000 / 3) / 100 = 66.66 kr
   * - Remainder: 20000 % 3 = 2 øre
   * - Person 1: 66.67 kr (base + 1 øre)
   * - Person 2: 66.67 kr (base + 1 øre)
   * - Person 3: 66.66 kr (base only)
   * - Total: 200.00 kr ✓
   *
   * @param total - Total amount to split (in kr)
   * @param splitCount - Number of ways to split
   * @param startPosition - Starting payer position (1-indexed)
   * @param numShares - Number of shares to pay for
   * @returns The amount this payer should pay (in kr)
   */
  calculateFairSplitAmount(
    total: number,
    splitCount: number,
    startPosition: number,
    numShares: number,
  ): number {
    if (splitCount <= 0 || numShares <= 0) {
      return 0;
    }

    // Convert to øre/cents for precise integer math
    const totalInOre = Math.round(total * 100);
    const baseAmountInOre = Math.floor(totalInOre / splitCount);
    const remainder = totalInOre % splitCount;

    let amountInOre = 0;
    for (let i = 0; i < numShares; i++) {
      const position = startPosition + i;
      // First 'remainder' positions get an extra øre
      amountInOre += baseAmountInOre + (position <= remainder ? 1 : 0);
    }

    return amountInOre / 100;
  }

  // internal mutable signal
  private _option = signal<SplitOption | null>(null);

  // expose a read‑only view
  readonly option = this._option.asReadonly();

  /** set a new option */
  select(opt: SplitOption) {
    this._option.set(opt);
  }

  resetSplitConfig() {
    this.splitCount.set(2);
    this.payForSplitCount.set(1);
    this.hideSplitCountControls.set(false);
    this.hidePayForSplitCountControls.set(false);
    this.maxPayForSplitCount.set(null);
  }

  /** clear the selection */
  reset() {
    this._option.set(null);
  }

  increaseSplitCount(): void {
    this.splitCount.set(this.splitCount() + 1);
  }

  decreaseSplitCount(): void {
    if (this.splitCount() > 2) {
      this.splitCount.set(this.splitCount() - 1);

      if (this.payForSplitCount() > this.splitCount()) {
        this.payForSplitCount.set(this.splitCount());
      }
    }
  }

  increasePayForSplitCount(): void {
    if (
      this.payForSplitCount() + 1 <= this.splitCount() &&
      (this.maxPayForSplitCount() === null ||
        this.payForSplitCount() + 1 <= this.maxPayForSplitCount()!)
    ) {
      this.payForSplitCount.set(this.payForSplitCount() + 1);
    }
  }

  decreasePayForSplitCount(): void {
    if (this.payForSplitCount() > 1) {
      this.payForSplitCount.set(this.payForSplitCount() - 1);
    }
  }
}
